import React from "react";
import { Button, Center, Dialog, Float, Image, Portal } from "@chakra-ui/react";
import { LuChevronLeft, LuChevronRight, LuX, LuYoutube } from "react-icons/lu";
import Slider from "react-slick";
import { getMediaSrc, useProductMedia } from "./ProductMediaContext";
import ProductMediaThumbnails from "./ProductMediaThumbnails";

const ProductFullscreenViewer = ({ onOpenVideo }) => {
    const sliderRef = React.useRef(null);
    const thumbnailsRef = React.useRef(null);
    const headerHeight = 56;
    const footerHeight = 88;

    const {
        images,
        totalMedia,
        openFullscreenSlider,
        fullscreenCurrentIndex,
        closeFullscreen,
        showPreviousFullscreenMedia,
        showNextFullscreenMedia,
        setFullscreenCurrentIndex,
    } = useProductMedia();

    const sliderSettings = React.useMemo(() => ({
        arrows: false,
        dots: false,
        infinite: totalMedia > 1,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        swipe: totalMedia > 1,
        draggable: totalMedia > 1,
        adaptiveHeight: false,
        initialSlide: fullscreenCurrentIndex,
        afterChange: (index) => {
            setFullscreenCurrentIndex(index);
        },
    }), [fullscreenCurrentIndex, setFullscreenCurrentIndex, totalMedia]);

    React.useEffect(() => {
        if (!openFullscreenSlider) {
            return;
        }

        requestAnimationFrame(() => {
            sliderRef.current?.slickGoTo(fullscreenCurrentIndex, true);
        });
    }, [fullscreenCurrentIndex, openFullscreenSlider]);

    React.useEffect(() => {
        if (!openFullscreenSlider) {
            return;
        }

        const activeThumbnail = thumbnailsRef.current?.querySelector(`[data-thumbnail-index="${fullscreenCurrentIndex}"]`);
        activeThumbnail?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, [fullscreenCurrentIndex, openFullscreenSlider]);

    const handleThumbnailSelect = React.useCallback((index) => {
        setFullscreenCurrentIndex(index);
    }, [setFullscreenCurrentIndex]);

    const handleActionClick = React.useCallback((callback) => (event) => {
        event.stopPropagation();
        callback?.();
    }, []);

    const renderMediaSlide = (media, index) => {
        if (media?.type === "video") {
            return (
                <div key={`${media?.id || media?.url || "media"}-${index}`} className="product-fullscreen-slide">
                    <div className="product-fullscreen-slide-content">
                        <Image src={media.url} fit="contain" maxH="100%" maxW="100%" />
                        <Float placement={"middle-center"} offsetX={16} offsetY={6}>
                            <Button size={"lg"} onClick={handleActionClick(() => onOpenVideo?.(media))}>
                                <LuYoutube size={64} />
                            </Button>
                        </Float>
                    </div>
                </div>
            );
        }

        return (
            <div key={`${media?.id || media?.url || "media"}-${index}`} className="product-fullscreen-slide">
                <Center className="product-fullscreen-slide-content">
                    <Image src={getMediaSrc(media)} fit="contain" maxH="100%" maxW="100%" />
                </Center>
            </div>
        );
    };

    return (
        <Dialog.Root
            lazyMount
            open={openFullscreenSlider}
            onOpenChange={(details) => {
                if (!details.open) {
                    closeFullscreen();
                }
            }}
        >
            <Portal>
                <style>{`
                    .product-fullscreen-slider,
                    .product-fullscreen-slider .slick-slider,
                    .product-fullscreen-slider .slick-list,
                    .product-fullscreen-slider .slick-track,
                    .product-fullscreen-slider .slick-slide,
                    .product-fullscreen-slider .slick-slide > div {
                        height: 100%;
                    }

                    .product-fullscreen-slider .slick-track {
                        display: flex;
                    }

                    .product-fullscreen-slider .slick-slide {
                        display: flex !important;
                        align-items: center;
                        justify-content: center;
                    }

                    .product-fullscreen-slide {
                        width: 100%;
                        height: 100%;
                    }

                    .product-fullscreen-slide-content {
                        width: 100%;
                        height: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        position: relative;
                        padding-top: var(--product-fullscreen-header-height);
                        padding-bottom: var(--product-fullscreen-footer-height);
                        box-sizing: border-box;
                    }

                `}</style>
                <Dialog.Backdrop bg="whiteAlpha.900" />
                <Dialog.Positioner>
                    <Dialog.Content bg="white" color="black" w="100vw" h="100dvh" maxW="100vw" borderRadius="0" m="0" overflow="hidden">
                        <Dialog.Body p={0} h="100dvh" overflow="hidden">
                            <div style={{ height: "100dvh", position: "relative", overflow: "hidden", background: "white", "--product-fullscreen-header-height": `${headerHeight}px`, "--product-fullscreen-footer-height": `${footerHeight}px` }}>
                                <div className="product-fullscreen-slider" style={{ position: "absolute", inset: 0, minHeight: 0 }}>
                                    <Slider ref={sliderRef} {...sliderSettings}>
                                        {images.map((media, index) => renderMediaSlide(media, index))}
                                    </Slider>

                                    <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3 }}>
                                        <Button
                                            size="sm"
                                            variant="solid"
                                            borderRadius="full"
                                            bg="whiteAlpha.900"
                                            color="black"
                                            boxShadow="md"
                                            display={{ base: "none", md: "inline-flex" }}
                                            onClick={handleActionClick(showPreviousFullscreenMedia)}
                                            disabled={totalMedia <= 1}
                                        >
                                            <LuChevronLeft />
                                        </Button>
                                    </div>

                                    <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3 }}>
                                        <Button
                                            size="sm"
                                            variant="solid"
                                            borderRadius="full"
                                            bg="whiteAlpha.900"
                                            color="black"
                                            boxShadow="md"
                                            display={{ base: "none", md: "inline-flex" }}
                                            onClick={handleActionClick(showNextFullscreenMedia)}
                                            disabled={totalMedia <= 1}
                                        >
                                            <LuChevronRight />
                                        </Button>
                                    </div>
                                </div>

                                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: headerHeight, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px", boxSizing: "border-box", background: "linear-gradient(to bottom, rgba(255,255,255,0.96), rgba(255,255,255,0.72), transparent)", zIndex: 4 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                                        {totalMedia > 0 ? `${fullscreenCurrentIndex + 1}/${totalMedia}` : ""}
                                    </div>
                                    <Button size="sm" variant="outline" bg="whiteAlpha.900" color="black" onClick={handleActionClick(closeFullscreen)}>
                                        <LuX />
                                    </Button>
                                </div>

                                <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: footerHeight, padding: "12px", boxSizing: "border-box", background: "linear-gradient(to top, rgba(255,255,255,0.98), rgba(255,255,255,0.82), transparent)", overflowX: "auto", overflowY: "hidden", zIndex: 4 }}>
                                    <ProductMediaThumbnails
                                        containerRef={thumbnailsRef}
                                        images={images}
                                        activeIndex={fullscreenCurrentIndex}
                                        onSelect={handleThumbnailSelect}
                                        layout="scroll"
                                    />
                                </div>
                            </div>
                        </Dialog.Body>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default ProductFullscreenViewer;